<template>
  <v-container>
    <v-row
      v-if="!radio && !$vuetify.breakpoint.xsOnly"
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Players
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.description"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
      <v-col cols="auto" />
    </v-row>

    <v-row class="flex-nowrap">
      <!--<editor-fold desc="Filters">-->
      <filters-column
        v-if="$vuetify.breakpoint.mdAndUp"
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <div v-if="!radio && $can('manage-all')">
          <v-subheader class="font-weight-medium">
            Client
          </v-subheader>
          <v-autocomplete
            v-model="filters.client_id"
            solo
            flat
            outlined
            :items="clients.data"
            :loading="clients.loading"
            clearable
            item-text="name"
            item-value="id"
            placeholder="Select client..."
            dense
          />
          <v-subheader class="font-weight-medium">
            Radio
          </v-subheader>
          <v-autocomplete
            v-model="filters.radio_id"
            solo
            flat
            outlined
            :items="radios.data"
            :loading="radios.loading"
            clearable
            item-text="title"
            item-value="id"
            placeholder="Select radio..."
            dense
          />
        </div>

        <v-subheader class="font-weight-medium">
          Status
        </v-subheader>

        <v-list
          rounded
          color="transparent"
          class="pa-0"
          dense
        >
          <v-list-item-group
            v-model="filters.player_status"
            class="ma-0"
          >
            <v-list-item
              class="mb-1"
              active-class="primary--text"
              :value="PLAYER_STATUS_ONLINE"
            >
              <template v-slot:default="{ active, toggle }">
                <v-list-item-action>
                  <v-simple-checkbox
                    color="primary"
                    :value="active"
                    :ripple="false"
                    @click="toggle"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    Online
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
            <v-list-item
              class="mb-1"
              active-class="primary--text"
              :value="PLAYER_STATUS_OFFLINE"
            >
              <template v-slot:default="{ active, toggle }">
                <v-list-item-action>
                  <v-simple-checkbox
                    color="primary"
                    :value="active"
                    :ripple="false"
                    @click="toggle"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    Offline
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
          :show-counts="selected.length === 0 && $can('manage-all')"
          :has-actions="selected.length > 0"
          :total-visible="8"
        >
          <template
            v-if="selected.length > 0"
            v-slot:action
          >
            <v-row
              dense
              class="caption"
              align="center"
            >
              <v-col
                cols="auto"
                style="min-width: 130px;"
              >
                <span>With <strong>{{ selected.length }} selected</strong></span>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  outlined
                  class="text-none mr-1"
                  small
                  color="secondary"
                  @click="assignToRadioDialog.visible = true"
                >
                  <v-icon
                    small
                    left
                  >
                    fa fa-plus
                  </v-icon>
                  Assign to Radio
                </v-btn>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="secondary"
                      small
                      class="mr-1"
                      :loading="syncing"
                      v-on="on"
                      @click="syncPlayers(selected.map(p => p.id))"
                    >
                      <v-icon small>
                        fa fa-sync
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Sync players content</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="secondary"
                      small
                      class="mr-1"
                      :loading="rebooting"
                      v-on="on"
                      @click="rebootPlayers(selected.map(p => p.id))"
                    >
                      <v-icon small>
                        fa fa-power-off
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Reboot players</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="secondary"
                      small
                      class="mr-1"
                      v-on="on"
                      @click="startFlash(selected.map(p => p.id))"
                    >
                      <v-icon small>
                        fa fa-download
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Flash players</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                      @click="deleteConfirmationDialogVisible = true"
                    >
                      <v-icon small>
                        fa fa-trash
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete players</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </template>
        </pagination>
        <v-data-table
          v-if="$can('manage-all') || !$vuetify.breakpoint.xsOnly"
          v-model="selected"
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          :mobile-breakpoint="0"
          :show-select="$can('manage-all')"
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: player, isSelected, select }">
            <tr>
              <td v-if="$can('manage-all')">
                <v-simple-checkbox
                  :value="isSelected"
                  class="show-on-hover"
                  :class="{ 'visible': isSelected }"
                  color="primary"
                  :ripple="false"
                  @input="select"
                />
              </td>
              <td>
                <router-link :to="{ name: 'player', params: { id: player.id }}">
                  <v-row no-gutters>
                    <v-col>
                      <div
                        v-if="player.description"
                        class="subtitle-2 primary--text"
                      >
                        {{ player.description }}
                      </div>
                      <div
                        v-else
                        class="subtitle-2 grey--text text--lighten-1"
                      >
                        Unknown
                      </div>
                    </v-col>
                    <v-col
                      v-if="!player.is_latest_firmware"
                      cols="auto"
                      class="pr-2"
                    >
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-chip
                            outlined
                            color="red darken-2"
                            class="text-caption"
                            x-small
                            v-on="on"
                          >
                            {{ player.firmware ? player.firmware.version : "unknown" }}
                          </v-chip>
                        </template>
                        <span>
                          Current firmware version is <strong v-if="player.latest_firmware">{{ player.latest_firmware.version }}</strong>
                        </span>
                      </v-tooltip>
                    </v-col>
                    <v-col
                      v-if="player.ssh_port && $dayjs(now).diff($dayjs(player.ssh_port_at), 'second') <= 30 && $can('manage-all')"
                      cols="auto"
                    >
                      <v-chip
                        outlined
                        color="orange darken-2"
                        class="text-caption"
                        small
                        @click.stop.prevent="copySshCommand(player.ssh_port)"
                      >
                        SSH: {{ player.ssh_port }}
                        <v-icon
                          right
                          x-small
                        >
                          far fa-copy
                        </v-icon>
                      </v-chip>
                    </v-col>
                    <v-col
                      v-if="player.sync_progress > 0 && player.sync_progress < 1"
                      cols="auto"
                    >
                      <v-chip
                        outlined
                        color="secondary"
                        class="text-caption"
                        small
                      >
                        <v-avatar left>
                          <v-progress-circular
                            :value="player.sync_progress * 100"
                            :width="2"
                            :size="16"
                          />
                        </v-avatar>
                        Downloading ({{ player.sync_progress | percent }})
                      </v-chip>
                    </v-col>
                    <v-col
                      v-if="player.current_action && $can('manage-all')"
                      cols="auto"
                    >
                      <v-tooltip
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-chip
                            outlined
                            small
                            :color="actionData[player.current_action].color || 'secondary'"
                            v-on="on"
                          >
                            <v-avatar
                              v-if="actionData[player.current_action].progress !== false"
                              left
                            >
                              <v-progress-circular
                                :width="2"
                                :size="16"
                                indeterminate
                              />
                            </v-avatar>
                            {{ actionData[player.current_action].text }}
                          </v-chip>
                        </template>
                        <span>
                          Reported start at {{ $dayjs(player.current_action_at).format('DD.MM.YYYY. HH:mm:ss') }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </router-link>
              </td>
              <td
                v-if="$can('manage-all')"
                style="min-width: 170px; max-width: 200px"
                class="text-no-wrap"
              >
                <router-link :to="{ name: 'player', params: { id: player.id }}">
                  <span v-if="player.client">
                    {{ player.client.name }}
                  </span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    N/A
                  </span>
                </router-link>
              </td>
              <td
                style="min-width: 170px; max-width: 200px"
              >
                <router-link
                  :is="$can('manage-all') ? 'router-link' : 'span'"
                  v-if="player.radio"
                  :to="$can('manage-all') ? { name: 'radio', params: { id: player.radio.id }} : undefined"
                >
                  <span v-if="player.radio.parent">
                    {{ player.radio.parent.title }} /
                  </span>
                  <span class="secondary--text">
                    {{ player.radio.title }}
                  </span>
                </router-link>
                <router-link
                  v-else
                  :to="{ name: 'player', params: { id: player.id }}"
                >
                  <span class="grey--text text--lighten-1">
                    n/a
                  </span>
                </router-link>
              </td>
              <td style="width: 1px">
                <router-link :to="{ name: 'player', params: { id: player.id }}">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        small
                        :color="player.player_status === PLAYER_STATUS_ONLINE ? 'green darken-1' : offlineChipColor(player.player_last_online_at)"
                        text-color="white"
                        dark
                        class="ma-0"
                        v-on="on"
                      >
                        {{ player.player_status === PLAYER_STATUS_ONLINE ? 'Online' : 'Offline' }}
                      </v-chip>
                    </template>
                    <span v-if="player.player_status === PLAYER_STATUS_ONLINE">
                      Status updated <strong>{{ $dayjs(player.player_status_at).from(now) }}</strong>, on {{ $dayjs(player.player_status_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </span>
                    <span v-else-if="player.player_status === PLAYER_STATUS_OFFLINE">
                      Last online <strong>{{ $dayjs(player.player_last_online_at).from(now) }}</strong>, on {{ $dayjs(player.player_last_online_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </span>
                  </v-tooltip>
                </router-link>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px;"
              >
                <router-link :to="{ name: 'player', params: { id: player.id }}">
                  <span v-if="player.last_sync_at">
                    {{ $dayjs(player.last_sync_at).format('DD.MM.YYYY. HH:mm:ss') }}
                  </span>
                  <span
                    v-else
                    class="grey--text text--lighten-1"
                  >
                    Never
                  </span>
                </router-link>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  v-if="$can('manage-all')"
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item @click="editPlayerDialog.player = player; editPlayerDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Details</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="syncPlayers([ player.id ])">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-sync
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Sync</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="rebootPlayers([ player.id ])">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-power-off
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Reboot</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="startFlash([ player.id ])">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-download
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Flash</v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="startSsh(player.id)">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-network-wired
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Start SSH</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="selected = [ player ]; deleteConfirmationDialogVisible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Delete</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div v-else>
          <mobile-player-card
            v-for="player in items.data"
            :key="player.id"
            :player="player"
            class="mb-2"
          />
        </div>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
            :show-counts="$can('manage-all')"
          />
        </div>
      </v-col>
    </v-row>

    <edit-player-dialog
      v-if="editPlayerDialog.player"
      :visible.sync="editPlayerDialog.visible"
      :player="editPlayerDialog.player"
      @player:updated="fetchItems()"
    />

    <assign-to-radio-dialog
      :visible.sync="assignToRadioDialog.visible"
      :player-ids="selected.map(p => p.id)"
      :radios="radios.data"
      @player:assigned="fetchItems()"
    />

    <flash-dialog
      :visible.sync="flashDialog.visible"
      :player-ids="flashDialog.playerIds"
      @player:flashed="fetchItems()"
    />

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deletePlayers(selected.map(p => p.id))"
    />
  </v-container>
</template>

<script>
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import FiltersColumn from '@/components/helpers/FiltersColumn'
import Clients from '@/services/Clients'
import { cloneDeep } from 'lodash'
import PlayerModel from '@/models/PlayerModel'
import Players from '@/services/Players'
import Radios from '@/services/Radios'
import EditPlayerDialog from '@/components/players/EditPlayerDialog'
import AssignToRadioDialog from '@/components/players/AssignToRadioDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import FlashDialog from '@/components/players/FlashDialog'
import Echo from '@/plugins/echo'
import MobilePlayerCard from '@/components/players/MobilePlayerCard'

export default {
  name: 'Players',
  components: {
    MobilePlayerCard,
    FlashDialog,
    ConfirmDeleteDialog,
    AssignToRadioDialog,
    EditPlayerDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin, deleteConfirmationMixin],
  props: {
    radio: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      selected: [],
      clients: {
        loading: false,
        data: []
      },
      radios: {
        loading: false,
        data: []
      },
      editPlayerDialog: {
        visible: false,
        player: undefined
      },
      flashDialog: {
        visible: false,
        playerIds: []
      },
      assignToRadioDialog: {
        visible: false,
        radioId: undefined
      },
      deleteConfirmationDialogVisible: false,
      syncing: false,
      rebooting: false,
      flashing: false,
      deleting: false,
      now: new Date()
    }
  },
  watch: {
    'filters.client_id': function () {
      this.fetchRadios()
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(PlayerModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Name', value: 'description' },
      ...(this.$can('manage-all') ? [{ text: 'Client', value: 'client_id' }] : []),
      { text: 'Radio', value: 'radio.name' },
      { text: 'Status', value: 'player_status' },
      { text: 'Last Sync', value: 'last_sync_at' },
      { sortable: false }
    ]

    if (this.radio) {
      this.filters.radio_id = this.radio.id
    }

    this.PLAYER_STATUS_ONLINE = PlayerModel.STATUS_ONLINE
    this.PLAYER_STATUS_OFFLINE = PlayerModel.STATUS_OFFLINE

    this.actionData = PlayerModel.actionData

    this.fetchClients()
    this.fetchRadios()
    this.fetchItems()

    Echo.private('superadmin').listen('PlayerUpdated', (player) => {
      const index = this.items.data.findIndex(p => p.id === player.id)

      if (index === -1) return

      this.items.data.splice(index, 1, player)
    })

    this.interval = setInterval(() => {
      this.now = new Date()
    }, 1000)
  },
  beforeDestroy() {
    Echo.leave('superadmin')

    clearInterval(this.interval)
  },
  methods: {
    offlineChipColor(lastOnlineAt) {
      if (!lastOnlineAt) return 'red darken-4'
      const diffDays = this.$dayjs().diff(this.$dayjs(lastOnlineAt), 'day')
      if (diffDays < 1) return 'yellow darken-1'
      if (diffDays < 30) return 'orange darken-1'
      if (diffDays < 90) return 'red lighten-1'
      return 'red darken-4'
    },
    async itemsFetcher(queryParams) {
      return await Players.fetch(queryParams)
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async fetchRadios() {
      this.radios.loading = true

      try {
        this.radios.data = (await Radios.fetch({
          client_id: this.filters.client_id || undefined
        }))
      } catch (e) {

      } finally {
        this.radios.loading = false
      }
    },
    async syncPlayers(playerIds) {
      this.syncing = true

      await Players.sync(playerIds)

      this.syncing = false

      this.$store.dispatch('alert', {
        text: 'Successfully sent signal to sync players',
        color: 'success'
      })
    },
    async rebootPlayers(playerIds) {
      this.rebooting = true

      await Players.reboot(playerIds)

      this.rebooting = false

      this.$store.dispatch('alert', {
        text: 'Successfully sent signal to reboot players',
        color: 'success'
      })
    },
    startFlash(playerIds) {
      this.flashDialog.visible = true
      this.flashDialog.playerIds = playerIds
    },
    // async flashPlayers(playerIds) {
    //   await Players.flash(playerIds, this.flash)
    // },
    async assignPlayersToRadio(playerIds, radioId) {
      await Players.assignToRadio(playerIds, radioId)

      this.$store.dispatch('alert', {
        text: 'Players successfuly assigned to radio',
        color: 'success'
      })

      this.fetchItems()
    },
    async deletePlayers(playerIds) {
      this.deleting = true

      await Players.delete(playerIds)

      this.deleting = false

      this.$store.dispatch('alert', {
        text: 'Players successfuly deleted',
        color: 'success'
      })

      this.selected = []

      this.deleteConfirmationDialogVisible = false

      this.fetchItems()
    },
    async startSsh(playerId) {
      await Players.startSshSession(playerId)

      this.$store.dispatch('alert', {
        text: 'SSH started',
        color: 'success'
      })
    },
    copySshCommand(port) {
      this.$copyText(`ssh -p ${port} root@127.0.0.1`)

      this.$store.dispatch('alert', {
        text: 'SSH shell command copied to clipboard',
        color: 'success'
      })
    }
  }
}
</script>
